import type { TSportEvent, TMarket } from 'markets-store'
import type {
  FilterConfig,
  ExtendedFilterConfig,
  MarketsInFilters,
} from 'markets-store/marketsbook'
import { sortBy, isTruthy } from '@st/utils'
import { useMainMarket } from '..'
import {
  getMarketsFromConfigLine,
  getAllPeriodMarketsFromConfigLine,
  hasPeriod,
} from '../../helpers'

interface PeriodLineReturn {
  minPeriod: number
  periodSpecifier: string
  items: (ExtendedFilterConfig | null)[]
}

interface SortedByPeriodReturn {
  periodSpecifier: string
  periodNumber: number
  market: TMarket[]
}

function sortAndGroupByPeriodNumber(items: TMarket[]): SortedByPeriodReturn {
  const firstMarket = items?.[0]
  if (!firstMarket)
    return {
      periodNumber: 0,
      periodSpecifier: '',
      market: [],
    }

  const periodNumberSpecifier = hasPeriod(firstMarket.specifiers)
  if (periodNumberSpecifier) {
    const sorted = sortBy(items, (item) => [
      // @ts-expect-error item.specifiers[periodnr]
      item.specifiers[periodNumberSpecifier],
    ])
    const firstMarketPeriodnumber = sorted[0].specifiers[periodNumberSpecifier]

    return {
      periodNumber: +(firstMarketPeriodnumber ?? 1),
      periodSpecifier: periodNumberSpecifier,
      market: sorted.filter(
        (market) =>
          market.specifiers[periodNumberSpecifier] === firstMarketPeriodnumber,
      ),
    }
  }
  return {
    periodNumber: 1,
    periodSpecifier: '',
    market: items,
  }
}

/* 
  чтобы в одной линии избежать колонок из
  разных периодов, нужно вернуть колонки с текущим
  минимальным доступным временным отрезком
*/
function getMinPeriodLine(periodsLine: PeriodLineReturn) {
  const { minPeriod, items, periodSpecifier } = periodsLine

  const filteredByPeriod = items.map((item) => {
    if (item?.linePeriod === minPeriod) return item
    return null
  })

  if (!filteredByPeriod.some(isTruthy)) return null

  return {
    periodSpecifier,
    minPeriod,
    filteredByPeriod,
  }
}

/* 
  собирает колонки предросписи из конфига ( markets.(1_half |  2_half))
  так как у некоторых спортов нет спецификаторов периода их маркеты группируют по половинам
*/
function getHalfsMarkets(
  filters: (FilterConfig | null)[],
  activeMarkets: TMarket[],
) {
  function getHalf(line: FilterConfig, halfNumber: number) {
    return getMarketsFromConfigLine({
      marketsFilter: line,
      marketList: activeMarkets,
      line: `${halfNumber} half` as keyof MarketsInFilters,
    })
  }
  const marketsByHalfs = filters.reduce<PeriodLineReturn>(
    (acc, line) => {
      if (!line) {
        acc.items.push(null)
        return acc
      }
      const firstHalfMarket = getHalf(line, 1)

      if (firstHalfMarket) {
        acc.minPeriod = 1
        acc.items.push({
          ...line,
          linePeriod: 1,
          lineMarkets: firstHalfMarket ?? [],
        })

        return acc
      }

      const secondHalfMarket = getHalf(line, 2)
      if (secondHalfMarket) {
        acc.minPeriod = 2
        acc.items.push({
          ...line,
          linePeriod: 2,
          lineMarkets: secondHalfMarket ?? [],
        })

        return acc
      }

      // возвращаем пустую колонку если нет маркетов
      acc.items.push(null)
      return acc
    },
    {
      minPeriod: 0,
      periodSpecifier: 'hf',
      items: [],
    },
  )

  return getMinPeriodLine(marketsByHalfs)
}

/* 
  собирает колонки предросписи из конфига ( markets.period ) для вывода маркетов
  и подбирает маркеты на первый доступный временной отрезок, так как у маркетов одинаковые id
  нужно сортировать их по специфаеру временного отрезка
*/
function getPeriodsMarkets(
  filters: (FilterConfig | null)[],
  activeMarkets: TMarket[],
) {
  const marketsByPeriod = filters.reduce<PeriodLineReturn>(
    (acc, line) => {
      if (!line) {
        acc.items.push(null)
        return acc
      }

      const marketsByIds = getAllPeriodMarketsFromConfigLine({
        marketsFilter: line,
        marketList: activeMarkets,
      })

      if (!marketsByIds?.length) {
        // возвращаем пустую колонку если нет маркетов
        acc.items.push(null)
        return acc
      }
      /* 
        сортировка по минимальному периоду из всех 
        доступных маркетов
      */
      const marketsByPeriods = marketsByIds
        .map(sortAndGroupByPeriodNumber)
        .filter((item) => item?.periodNumber)

      const firstInSortedByPeriod = sortBy(marketsByPeriods, (item) => [
        item.periodNumber,
      ])[0]

      if (!firstInSortedByPeriod) return acc
      acc.periodSpecifier = firstInSortedByPeriod.periodSpecifier
      /* 
        определяем какой минимальный доступный отрезок
        можем отобразить
      */
      acc.minPeriod = acc.minPeriod
        ? Math.min(firstInSortedByPeriod.periodNumber, acc.minPeriod)
        : firstInSortedByPeriod.periodNumber

      acc.items.push({
        ...line,
        linePeriod: firstInSortedByPeriod.periodNumber,
        lineMarkets: firstInSortedByPeriod.market,
      })
      return acc
    },
    {
      minPeriod: 0,
      periodSpecifier: '',
      items: [],
    },
  )

  return getMinPeriodLine(marketsByPeriod)
}

export function usePeriodMarkets(
  event: Ref<TSportEvent>,
  filters: Ref<(FilterConfig | null)[] | undefined>,
) {
  const { t } = useI18n()

  const { activeMarkets } = useMainMarket(event)

  const mainLine = computed<(ExtendedFilterConfig | null)[] | null>(() => {
    if (!filters.value) return null

    return filters.value.map((filter) => {
      if (!filter) return null

      const markets =
        getMarketsFromConfigLine({
          marketsFilter: filter,
          marketList: activeMarkets.value,
          line: 'main',
        }) ?? []

      return {
        ...filter,
        lineName: 'main' as const,
        lineMarkets: markets,
      }
    })
  })

  const periodLine = computed(() => {
    if (!filters.value) return null

    return (
      getPeriodsMarkets(filters.value, activeMarkets.value) ??
      getHalfsMarkets(filters.value, activeMarkets.value)
    )
  })
  const currentPeriodNumber = computed(() => periodLine.value?.minPeriod ?? '')
  const currentPeriodSpecifier = computed(
    () => periodLine.value?.periodSpecifier ?? '',
  )

  const periodLineLabel = computed(() => {
    const periodName =
      {
        hf: t('specifiers.hf'),
        half: t('specifiers.half'),
        setnr: t('specifiers.setnr'),
        quarternr: t('specifiers.quarternr'),
        periodnr: t('specifiers.periodnr'),
        mapnr: t('specifiers.mapnr'),
        gamenr: t('specifiers.setnr'),
        inningnr: t('specifiers.inning'),
        overnr: t('specifiers.overnr'),
        framenr: t('specifiers.frame'),
        roundnr: t('specifiers.roundnr'),
        deliverynr: t('specifiers.deliverynr'),
        number: t('specifiers.number'),
      }[currentPeriodSpecifier.value] ?? ''

    return `${currentPeriodNumber.value} ${periodName}`
  })

  const isPeriodLineActive = computed(() => !!periodLine.value)

  return {
    periodLine,
    mainLine,
    isPeriodLineActive,
    periodLineLabel,
  }
}
